import { useState, useEffect, useCallback } from "react";
import { useBuilderOverlay } from "@printtat/builder";
import { designsApi, type DesignListItem } from "../../services/designsApi";
import { mockupApi } from "../../services/mockupApi";
import { getDefaultMockupPath } from "../../utils/productAssets";
import { BaseProductPicker } from "./BaseProductPicker";
import {
  PageHeader,
  PageContent,
  EmptyState,
  Button,
  IconButton,
  AlertDialog,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Tooltip,
} from "@printtat/ui";
import {
  Plus,
  Trash2,
  ShoppingBag,
  MoreVertical,
  Pencil,
  Loader2,
  Image,
  X,
  ChevronLeft,
  ChevronRight,
  Clock,
  Layers,
} from "lucide-react";
import type { SurfaceRenderResult, RenderGarmentResponse } from "@printtat/shared";
import { PricingDetails } from "@/shared/components/PricingBreakdown";
import { VariantPicker } from "@/modules/cart";
import { useCartStore } from "@/stores/cartStore";
import { ShoppingCart } from "lucide-react";

const surfaceDisplayName: Record<string, string> = {
  leftSleeve: "Left Sleeve",
  rightSleeve: "Right Sleeve",
};

const formatSurfaceName = (s: string) =>
  surfaceDisplayName[s] ?? s.charAt(0).toUpperCase() + s.slice(1);

export const ProductsPage = () => {
  const openBuilder = useBuilderOverlay((s) => s.openBuilder);
  const [designs, setDesigns] = useState<DesignListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [mockupRenders, setMockupRenders] = useState<
    Record<string, SurfaceRenderResult[]>
  >({});
  const [generatingMockup, setGeneratingMockup] = useState<string | null>(null);
  const [mockupCurrentMap, setMockupCurrentMap] = useState<Record<string, boolean>>({});
  const [lightbox, setLightbox] = useState<{
    images: string[];
    index: number;
  } | null>(null);
  const [cardSlideIndex, setCardSlideIndex] = useState<Record<string, number>>(
    {},
  );
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [cartDesign, setCartDesign] = useState<DesignListItem | null>(null);
  const addToCart = useCartStore((s) => s.addItem);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const items = await designsApi.list();
      setDesigns(items);

      const renderEntries = await Promise.allSettled(
        items.map((d) => mockupApi.getDesignRenders(d.id)),
      );
      const renders: Record<string, SurfaceRenderResult[]> = {};
      renderEntries.forEach((result, i) => {
        if (result.status === "fulfilled") {
          const actual = result.value.surfaces.filter(
            (s) => s.source !== "default_cdn" && s.previewImageUrl,
          );
          if (actual.length > 0) {
            renders[items[i].id] = actual;
          }
        }
      });
      setMockupRenders(renders);
    } catch {
      setError("Failed to load your products");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Check mockup freshness for all designs with surface exports
  useEffect(() => {
    if (designs.length === 0) return;
    const withExports = designs.filter((d) => d.surfaceExports && Object.keys(d.surfaceExports).length > 0);
    if (withExports.length === 0) return;

    const checks = withExports.map(async (d) => {
      try {
        const res = await mockupApi.isMockupCurrent(d.id, d.surfaceExports!);
        return [d.id, res] as const;
      } catch {
        return [d.id, false] as const;
      }
    });

    Promise.all(checks).then((results) => {
      const map: Record<string, boolean> = {};
      for (const [id, current] of results) map[id] = current;
      setMockupCurrentMap(map);
    });
  }, [designs]);

  const surfaceKeyMap: Record<string, string> = {
    front: "tshirt_front_chest",
    back: "tshirt_back_print",
    collar: "tshirt_collar_label",
    leftSleeve: "tshirt_left_sleeve",
    rightSleeve: "tshirt_right_sleeve",
  };

  const reverseKeyMap: Record<string, string> = Object.fromEntries(
    Object.entries(surfaceKeyMap).map(([k, v]) => [v, k]),
  );

  const getCardImages = (design: DesignListItem) => {
    const surfaces = design.productSurfaces ?? [];
    const renders = mockupRenders[design.id] ?? [];
    const renderBySurface: Record<string, string> = {};
    for (const r of renders) {
      if (r.previewImageUrl) {
        const builderSurface = reverseKeyMap[r.surfaceKey];
        if (builderSurface) renderBySurface[builderSurface] = r.previewImageUrl;
      }
    }
    const designedSurfaces = new Set(design.surfacesUsed ?? []);
    return surfaces
      .filter((surface) => designedSurfaces.has(surface))
      .map((surface) => ({
        surface,
        url:
          renderBySurface[surface] ??
          getDefaultMockupPath(design.productSlug ?? "", surface),
        isRender: !!renderBySurface[surface],
      }));
  };

  const handleGenerateMockup = async (design: DesignListItem) => {
    if (
      !design.surfaceExports ||
      Object.keys(design.surfaceExports).length === 0
    )
      return;

    setGeneratingMockup(design.id);
    try {
      const garmentId = "classic_tshirt";
      const surfaces = Object.entries(design.surfaceExports)
        .filter(([surface]) => surfaceKeyMap[surface])
        .map(([surface, url]) => ({
          surfaceKey: surfaceKeyMap[surface],
          customDesignUrl: url,
        }));

      const { jobId } = await mockupApi.render({
        garmentId,
        designId: design.id,
        productId: design.productId,
        surfaces,
      });

      // Poll for job completion
      const poll = async (): Promise<RenderGarmentResponse> => {
        const job = await mockupApi.getRenderJobStatus(jobId);
        if (job.status === "completed" && job.result) return job.result;
        if (job.status === "failed") throw new Error(job.error ?? "Render failed");
        await new Promise((r) => setTimeout(r, 2000));
        return poll();
      };

      const result = await poll();

      const actualRenders = result.surfaces.filter(
        (s) => s.source !== "default_cdn" && s.previewImageUrl,
      );
      setMockupRenders((prev) => ({
        ...prev,
        [design.id]: actualRenders,
      }));

      const primaryPreview = result.surfaces.find(
        (s) => s.previewImageUrl && s.source !== "default_cdn",
      )?.previewImageUrl;
      if (primaryPreview) {
        await designsApi.update(design.id, { previewUrl: primaryPreview });
        setDesigns((prev) =>
          prev.map((d) =>
            d.id === design.id ? { ...d, previewUrl: primaryPreview } : d,
          ),
        );
      }
      setMockupCurrentMap((prev) => ({ ...prev, [design.id]: true }));
    } catch {
      // TODO: replace with toast
    } finally {
      setGeneratingMockup(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await designsApi.delete(id);
      setDesigns((prev) => prev.filter((d) => d.id !== id));
    } catch {
      // TODO: replace with toast
    }
  };

  return (
    <PageContent>
      <PageHeader
        icon={ShoppingBag}
        title="Products"
        description="Manage and preview your designs"
        actions={
          <Button onClick={() => setShowPicker(true)}>
            <Plus size={16} />
            New Product
          </Button>
        }
      />

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i}>
              <div className="animate-pulse rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gray-100 aspect-[4/3]" />
                <div className="p-4 space-y-2.5">
                  <div className="flex justify-between">
                    <div className="bg-gray-100 h-4 rounded w-2/3" />
                    <div className="bg-gray-100 h-4 rounded w-1/5" />
                  </div>
                  <div className="bg-gray-100 h-3 rounded w-1/3" />
                  <div className="flex gap-1.5 pt-1">
                    <div className="bg-gray-100 h-5 rounded-full w-14" />
                    <div className="bg-gray-100 h-5 rounded-full w-12" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && !isLoading && (
        <div className="text-center py-12 text-gray-500">{error}</div>
      )}

      {/* Empty state */}
      {!isLoading && !error && designs.length === 0 && (
        <EmptyState
          icon={ShoppingBag}
          title="No products yet"
          description="Create your first product by choosing a base product and designing it"
          action={
            <Button onClick={() => setShowPicker(true)}>
              <Plus size={16} />
              New Product
            </Button>
          }
        />
      )}

      {/* Design grid */}
      {!isLoading && !error && designs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5 items-start">
          {designs.map((design) => (
            <div
              key={design.id}
              className="group relative bg-white rounded-xl border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all duration-200"
            >
              {/* Surface image slider */}
              {(() => {
                const images = getCardImages(design);
                const idx = cardSlideIndex[design.id] ?? 0;
                const current = images[idx];
                const allUrls = images.map((i) => i.url);
                return (
                  <div className="relative">
                    <div
                      className="bg-gradient-to-br from-gray-50 to-gray-100 aspect-square flex items-center justify-center overflow-hidden rounded-t-xl relative cursor-pointer"
                      onClick={() => setLightbox({ images: allUrls, index: idx })}
                    >
                      {current ? (
                        <img
                          key={current.url}
                          src={current.url}
                          alt={`${design.name} – ${current.surface}`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform animate-in fade-in zoom-in-95 duration-500"
                        />
                      ) : (
                        <ShoppingBag size={36} className="text-gray-200" />
                      )}
                      {images.length > 1 && (
                        <>
                          <IconButton
                            variant="outline"
                            size="xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCardSlideIndex((prev) => ({
                                ...prev,
                                [design.id]:
                                  (idx - 1 + images.length) % images.length,
                              }));
                            }}
                            className="absolute left-1.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 hover:bg-white"
                          >
                            <ChevronLeft size={16} />
                          </IconButton>
                          <IconButton
                            variant="outline"
                            size="xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCardSlideIndex((prev) => ({
                                ...prev,
                                [design.id]: (idx + 1) % images.length,
                              }));
                            }}
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 hover:bg-white"
                          >
                            <ChevronRight size={16} />
                          </IconButton>
                        </>
                      )}
                      {images.length > 1 && (
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 py-1 px-2 rounded-full bg-black/30 backdrop-blur-sm flex gap-1">
                          {images.map((_: unknown, i: number) => (
                            <span
                              key={i}
                              className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? "bg-white" : "bg-white/40"}`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Content */}
              <div className="p-4 flex flex-col">
                <div className="min-w-0">
                  <h3
                    onClick={() => openBuilder({ designId: design.id, productSlug: design.productSlug ?? undefined, onSave: () => load() })}
                    className="text-sm font-semibold text-gray-900 truncate cursor-pointer hover:text-indigo-600 transition-colors duration-150"
                  >
                    {design.name}
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {design.productName || "Unknown product"}
                  </p>
                </div>

           
                <div className="flex flex-col items-start gap-2 mt-3 text-[11px] text-gray-400 leading-none">
                  {design.surfacesUsed?.length > 0 &&
                    (() => {
                      const names = design.surfacesUsed.map(formatSurfaceName);
                      const visible = names.slice(0, 3);
                      const remaining = names.slice(3);
                      return (
                        <span className="flex items-center gap-1.5">
                          <Layers size={11} className="flex-shrink-0" />
                          {visible.join(", ")}
                          {remaining.length > 0 && (
                            <Tooltip
                              content={
                                <div>
                                  <span className="font-medium text-gray-900">
                                    Designed surfaces:
                                  </span>
                                  <br />
                                  {names.join(", ")}
                                </div>
                              }
                              side="bottom"
                            >
                              <span className="inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 rounded-full bg-gray-200 text-[10px] font-semibold text-gray-500 cursor-default hover:bg-gray-300 transition-colors">
                                +{remaining.length}
                              </span>
                            </Tooltip>
                          )}
                        </span>
                      );
                    })()}
                  <span className="flex items-center gap-1.5">
                    <Clock size={11} />
                    {new Date(design.updatedAt).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </div>

                {design.totalPrice != null && design.priceBreakdown && (
                  <div className="mt-4">
                    <PricingDetails
                      breakdown={design.priceBreakdown}
                      defaultOpen={false}
                      shadow={false}
                    />
                  </div>
                )}

                <Button
                  color="dark"
                  size="sm"
                  onClick={() => setCartDesign(design)}
                  className="w-full mt-2"
                >
                  <ShoppingCart size={12} />
                  Add to Cart
                </Button>
              </div>

              {/* Top-right actions */}
              <div className="absolute top-3 right-3 flex items-center gap-1.5">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      variant="ghost"
                      size="xs"
                      className="backdrop-blur-sm bg-white/80 hover:bg-white shadow-sm"
                    >
                      <MoreVertical size={14} className="text-gray-400" />
                    </IconButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40 bg-white">
                    <DropdownMenuItem
                      className="gap-2 text-xs cursor-pointer"
                      onClick={() => openBuilder({ designId: design.id, productSlug: design.productSlug ?? undefined, onSave: () => load() })}
                    >
                      <Pencil size={12} />
                      Edit
                    </DropdownMenuItem>
                    {design.surfaceExports && (
                      <DropdownMenuItem
                        className="gap-2 text-xs cursor-pointer"
                        disabled={generatingMockup === design.id || mockupCurrentMap[design.id]}
                        onClick={() => handleGenerateMockup(design)}
                      >
                        {generatingMockup === design.id ? (
                          <>
                            <Loader2 size={12} className="animate-spin" />
                            Generating...
                          </>
                        ) : mockupCurrentMap[design.id] ? (
                          <>
                            <Image size={12} />
                            Mockup Up to Date
                          </>
                        ) : (
                          <>
                            <Image size={12} />
                            Generate Mockup
                          </>
                        )}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      className="gap-2 text-xs cursor-pointer"
                      onClick={() => setCartDesign(design)}
                    >
                      <ShoppingCart size={12} />
                      Add to Cart
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="gap-2 text-xs text-red-600 cursor-pointer focus:text-red-600 focus:bg-red-50"
                      onClick={() => setDeleteConfirm(design.id)}
                    >
                      <Trash2 size={12} />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={deleteConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirm(null);
        }}
        title="Delete product"
        description="Are you sure you want to delete this product? This action cannot be undone."
        confirmText="Delete"
        variant="destructive"
        onConfirm={() => {
          if (deleteConfirm) handleDelete(deleteConfirm);
          setDeleteConfirm(null);
        }}
      />

      {/* Variant picker for add to cart */}
      {cartDesign && (
        <VariantPicker
          open={!!cartDesign}
          onOpenChange={(open) => {
            if (!open) setCartDesign(null);
          }}
          productId={cartDesign.productId}
          designName={cartDesign.name}
          productName={cartDesign.productName ?? undefined}
          previewImages={getCardImages(cartDesign).map((i) => i.url)}
          basePrice={cartDesign.totalPrice ?? 0}
          onAddToCart={async (selections) => {
            for (const s of selections) {
              await addToCart(cartDesign.id, s.variantId, s.quantity);
            }
            setCartDesign(null);
          }}
        />
      )}

      {/* Base product picker modal */}
      {showPicker && (
        <BaseProductPicker
          onClose={() => setShowPicker(false)}
          onSave={() => load()}
        />
      )}

      {/* Image lightbox overlay */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
        >
          <IconButton
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white"
          >
            <X size={20} />
          </IconButton>

          {lightbox.images.length > 1 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((prev) =>
                  prev
                    ? {
                        ...prev,
                        index:
                          (prev.index - 1 + prev.images.length) %
                          prev.images.length,
                      }
                    : null,
                );
              }}
              size="md"
              className="absolute left-4 bg-white/10 hover:bg-white/20 text-white"
            >
              <ChevronLeft size={24} />
            </IconButton>
          )}

          <img
            src={lightbox.images[lightbox.index]}
            alt="Preview"
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          />

          {lightbox.images.length > 1 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((prev) =>
                  prev
                    ? {
                        ...prev,
                        index: (prev.index + 1) % prev.images.length,
                      }
                    : null,
                );
              }}
              size="md"
              className="absolute right-4 bg-white/10 hover:bg-white/20 text-white"
            >
              <ChevronRight size={24} />
            </IconButton>
          )}

          {lightbox.images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-medium">
              {lightbox.index + 1} / {lightbox.images.length}
            </div>
          )}
        </div>
      )}
    </PageContent>
  );
};
